import type { Place } from '@types';
import SectionCallToAction from 'components/sections/sectionAppBanner/SectionCallToAction';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { IconType, KioskType } from '../../../@types';
import { AppContext } from '../../../context/app';
import useIsMobile from '../../../hooks/useIsMobile';
import { useKioskManager } from '../../../hooks/useKioskManager';
import { useNetworkManager } from '../../../hooks/useNetworkManager';
import { getPinCoordinate } from '../../../utils/getPinCoordinates';
import { imageResize } from '../../../utils/images';
import { ensureHttps } from '../../../utils/url';
import LocationDetailCard from '../../shared/locationDetailCard/LocationDetailCard';
import { LocationDetailsPopup } from '../../shared/locationDetailsPopup/LocationDetailsPopup';
import type { MapViewHandles } from '../../shared/mapView/MapView';
import MapView from '../../shared/mapView/MapView';
import { Modal } from '../../shared/modal/Modal';
import Pin from '../../shared/pin/Pin';
import Select from '../../shared/select/Select';
import Title from '../../shared/title/Title';
import { IconWithCoordinates } from '../../shared/youAreHerePin/IconWithCoordinates';
import styles from './sectionFilterMap.module.scss';

const MAX_POIS = 50;

const SectionFilterMap: React.FC = () => {
  const mapViewRef = useRef<MapViewHandles>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const locationCardRef = useRef<HTMLDivElement>(null);
  const { t: commonTranslation } = useTranslation('common');
  const { t: campusDirectoryTranslation } = useTranslation('campusDirectory');
  const [imgWidth, setImgWidth] = useState(0);
  const [imgHeight, setImgHeight] = useState(0);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [pins, setPins] = useState<Place[]>([]);
  const [youAreHereLocation, setYouAreHereLocation] = useState<any>();
  const {
    selectedSite,
    selectedBuilding,
    selectedFloor,
    map,
    selectedLocation,
    setMap,
    setSelectedBuilding,
    setSelectedFloor,
    setSelectedLocation,
    showKeyboard,
    mapZoomLevel,
  } = useContext(AppContext);
  const { kiosk, kioskMode, kioskRegistration } = useKioskManager();
  const { prioritizedPlaces } = useNetworkManager();
  const mapImageRef = useRef<HTMLImageElement | null>(null);
  const pinsRef = useRef<Place[]>([]);
  const renderedPinsRef = useMemo(() => {
    const refs: { [key: number]: React.RefObject<HTMLDivElement> } = {};
    pins.forEach((pin) => {
      refs[pin.id] = React.createRef();
    });
    return refs;
  }, [pins]);
  const isMobile = useIsMobile();

  const [currentTranslation, setCurrentTranslation] = useState({ x: 0, y: 0 });

  const buildingOptions = useMemo<{ value: number; label: string }[]>(() => {
    if (selectedSite) {
      const allBuildings = {
        value: 0,
        label: campusDirectoryTranslation('labels.allBuildings', {
          ns: 'campusDirectory',
        }),
      };
      const options = selectedSite.buildings.map((b) => ({
        value: b.id,
        label: b.name,
      }));
      return [allBuildings, ...options];
    }

    return [];
  }, [selectedSite, campusDirectoryTranslation]);

  const floorOptions = useMemo(
    () =>
      selectedBuilding?.floors
        .sort((a, b) => a.id - b.id)
        .map((f) => ({
          value: f.id,
          label: f.name,
        })) ?? [],
    [selectedBuilding],
  );

  const calculatePinCoordinates = (
    translation = { x: 0, y: 0 },
    zoomLevel = 1,
  ) => {
    if (translation !== currentTranslation) setCurrentTranslation(translation);
    const calculatedPins =
      prioritizedPlaces
        ?.filter(
          (p) =>
            (!kioskMode && // Web App building selected
              p.location.site === selectedSite?.id &&
              p.location.building === selectedBuilding?.id &&
              p.location.floor === selectedFloor?.id &&
              p.visible === 'always') ||
            (!kioskMode && // Web App "All Buildings" selected
              !selectedBuilding &&
              !selectedFloor &&
              p.location.site === selectedSite?.id &&
              p.visible === 'always') ||
            (kioskMode && // Kiosk building selected
              p.location.site === selectedSite?.id &&
              p.location.building === selectedBuilding?.id &&
              p.location.floor === selectedFloor?.id &&
              ['onsite', 'always'].includes(p.visible)) ||
            (kioskMode && // Kiosk "All Buildings" selected
              !selectedBuilding &&
              !selectedFloor &&
              ['onsite', 'always'].includes(p.visible)),
        )
        .map((p) => {
          const transformedPosition = getPinCoordinate(
            p.x,
            p.y,
            selectedFloor,
            selectedSite,
            imgHeight,
            imgWidth,
            translation,
            zoomLevel,
          );
          return {
            ...p,
            ...transformedPosition,
          };
        }) ?? [];
    setPins(calculatedPins);

    setYouAreHereLocation(() => {
      if (!kioskRegistration || !imgWidth || !imgHeight) {
        return undefined;
      }
      if (selectedFloor) {
        if (
          kioskRegistration.floor_id === selectedFloor.id &&
          kioskRegistration.building_id === selectedBuilding?.id
        ) {
          return getPinCoordinate(
            kioskRegistration.x,
            kioskRegistration.y,
            selectedFloor,
            selectedSite,
            imgHeight,
            imgWidth,
            translation,
            zoomLevel,
          );
        }
        return undefined;
      } else if (selectedSite && kioskRegistration) {
        return getPinCoordinate(
          kioskRegistration.x,
          kioskRegistration.y,
          selectedFloor,
          selectedSite,
          imgHeight,
          imgWidth,
          translation,
          zoomLevel,
        );
      }
    });
  };

  const handleResize = () => {
    if (mapImageRef.current) {
      setImgWidth(mapImageRef.current.offsetWidth);
      setImgHeight(mapImageRef.current.offsetHeight);
    }
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    mapViewRef.current?.resetToDefaultPosition();
  }, [map, selectedFloor, selectedBuilding]);

  useEffect(() => {
    if (!mapImageRef.current) return;
    if (
      imgWidth !== mapImageRef.current.offsetWidth ||
      imgHeight !== mapImageRef.current.offsetHeight
    ) {
      setImgWidth(mapImageRef.current.offsetWidth);
      setImgHeight(mapImageRef.current.offsetHeight);
    }
  }, [imgHeight, imgWidth, mapImageRef]);

  useEffect(() => {
    calculatePinCoordinates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgHeight, imgWidth]);

  const handleOnChangeBuilding = useCallback(
    (value: string | number) => {
      if (
        !selectedLocation ||
        (selectedLocation && selectedLocation?.location.building !== value)
      ) {
        const building = selectedSite?.buildings.find((b) => b.id === value);
        setSelectedBuilding(building || undefined);
        setSelectedFloor(
          building?.floors.sort((a, b) => a.id - b.id)[0] || undefined,
        );
        setMap(
          ensureHttps(
            building?.floors.sort((a, b) => a.id - b.id)[0].map_url ||
              selectedSite?.campus_map?.map_url,
          ),
        );
      }
    },
    [
      selectedLocation,
      selectedSite?.buildings,
      selectedSite?.campus_map?.map_url,
      setMap,
      setSelectedBuilding,
      setSelectedFloor,
    ],
  );

  const handleOnChangeFloor = useCallback(
    (value: string | number) => {
      if (
        !selectedLocation ||
        (selectedLocation && selectedLocation?.location.floor !== value)
      ) {
        const floor = selectedBuilding?.floors
          .sort((a, b) => a.id - b.id)
          .find((b) => value === b.id);
        setMap(
          ensureHttps(floor?.map_url || selectedSite?.campus_map?.map_url),
        );
        setSelectedFloor(floor);
      }
    },
    [
      selectedBuilding?.floors,
      selectedLocation,
      selectedSite?.campus_map?.map_url,
      setMap,
      setSelectedFloor,
    ],
  );

  const onMapLoad = (event: any) => {
    setImgWidth(event.target.offsetWidth);
    setImgHeight(event.target.offsetHeight);
  };

  const visiblePins = useMemo(() => {
    const filteredPins = pins
      .filter((pin) => !(!pin.x || !pin.y) && !(pin.x < 0 || pin.y < 0))
      .sort((a, b) => b.priority - a.priority);

    pinsRef.current = filteredPins;

    return filteredPins.map((pin) => {
      const pinRef = renderedPinsRef[pin.id];
      const pinElement: any = (
        <Pin
          key={pin.id}
          location={pin}
          x={pin.x ?? 0}
          y={pin.y ?? 0}
          mapContainerRef={mapContainerRef}
          ref={pinRef}
          visible={false}
        />
      );
      return pinElement;
    });
  }, [pins, renderedPinsRef]);

  /**
   * Declutters pins on the map by hiding overlapping pins.
   *
   * This function iterates through the visible pins, calculates their bounding
   * rectangles, and hides pins that overlap with others. If a pin has the
   * attribute `data-modal-open` set to 'true', it will remain visible and
   * the overlapping pin will be hidden instead.
   *
   * @remarks
   * - Pins are considered visible if their bounding rectangle's x and y
   *   coordinates are non-negative.
   * - Pins are hidden by setting their `style.visibility` to 'hidden'.
   * - Pins with `data-modal-open` attribute set to 'true' are given priority
   *   to remain visible.
   *
   * @returns {void}
   */
  const declutterPins = () => {
    const mapPaintedPinAndLabel = (pinAndLabel: any) => ({
      ...pinAndLabel,
      boundingClientRect: pinAndLabel.ref.current.getBoundingClientRect(),
      visible: true,
    });

    const paintedPinsAndLabels = visiblePins
      .filter((pin) => !!pin.ref.current)
      .map(mapPaintedPinAndLabel)
      .filter(
        (pinAndLabel) =>
          pinAndLabel.boundingClientRect.x >= 0 &&
          pinAndLabel.boundingClientRect.y >= 0,
      );

    for (let i = 0; i < paintedPinsAndLabels.length; i++) {
      const a = paintedPinsAndLabels[i];
      if (!a.visible) continue;

      for (let j = i + 1; j < paintedPinsAndLabels.length; j++) {
        const b = paintedPinsAndLabels[j];
        if (!b.visible) continue;

        if (
          !(
            a.boundingClientRect.right <= b.boundingClientRect.left ||
            a.boundingClientRect.left >= b.boundingClientRect.right ||
            a.boundingClientRect.bottom <= b.boundingClientRect.top ||
            a.boundingClientRect.top >= b.boundingClientRect.bottom
          )
        ) {
          if (!(b.ref.current.getAttribute('data-modal-open') === 'true')) {
            b.visible = false;
            b.ref.current.style.visibility = 'hidden';
          } else {
            a.visible = false;
            a.ref.current.style.visibility = 'hidden';
            b.visible = true;
            b.ref.current.style.visibility = 'visible';
          }
        }
      }
      if (
        a.visible ||
        a.ref.current.getAttribute('data-modal-open') === 'true'
      ) {
        a.ref.current.style.visibility = 'visible';
      }
    }
  };

  // Pins and labels are rendered asynchronously, so we need to wait for them to
  // be painted before decluttering them. Otherwise, the bounding rectangles
  // will not be accurate.
  requestAnimationFrame(() =>
    setTimeout(() => {
      declutterPins();
      if (!declutteredPins.length) calculatePinCoordinates();
    }, 1),
  );

  const displayDetailPopupModalHandler = (display: boolean) => {
    if (display) {
      // Safari overlooks overflow when rendering, so on Safari it might not work well
      document.body.style.position = 'relative'; // class to help prevent the scrolling on Safari
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.position = 'initial';
      document.body.style.overflow = '';
    }
    setShowDetailModal(display);
  };

  const calculateCardPosition = useCallback(
    (
      mapRect: { width: number; height: number },
      pinX: number,
      pinY: number,
      cardWidth: number,
      cardHeight: number,
      zoomLevel: number,
    ) => {
      const margin = 4;
      const zoomControlWidth = 80;
      const iconSize = 20;

      if (isMobile) {
        return {
          left: (mapRect.width - cardWidth) / 2,
          top: (mapRect.height - cardHeight) / 2,
        };
      }

      const horizontalSpaceIsOnTheLeft = pinX > mapRect.width / 2;
      const cardLeft = horizontalSpaceIsOnTheLeft
        ? Math.max(margin, pinX - cardWidth - margin)
        : pinX + margin;

      const verticalSpaceIsAbove = pinY > mapRect.height / 2;
      const cardTop = verticalSpaceIsAbove
        ? Math.max(margin, pinY - cardHeight - margin - iconSize)
        : pinY + margin;

      const adjustedLeft = Math.min(
        Math.max(cardLeft, margin),
        mapRect.width - cardWidth - margin - zoomControlWidth,
      );
      const adjustedTop = Math.min(
        Math.max(cardTop + iconSize, margin),
        mapRect.height - cardHeight - margin,
      );
      return { left: adjustedLeft, top: adjustedTop };
    },
    [isMobile],
  );

  const isPinVisibleWithinMapContainer = useCallback(
    (pinElement: any, mapContainerElement: any) => {
      if (!pinElement || !mapContainerElement) return false;

      const pinRect = pinElement.getBoundingClientRect();
      const mapRect = mapContainerElement.getBoundingClientRect();

      // Check if the pin is within the map container's visible area
      const isVisible =
        pinRect.right > mapRect.left &&
        pinRect.left < mapRect.right &&
        pinRect.bottom > mapRect.top &&
        pinRect.top < mapRect.bottom;

      if (!isVisible) {
        return false;
      }

      // Calculate the pin's relative position to the map container
      const relativeX = pinRect.left - mapRect.left;
      const relativeY = pinRect.top - mapRect.top;

      return { x: relativeX, y: relativeY };
    },
    [],
  );

  useEffect(() => {
    if (
      selectedLocation &&
      locationCardRef.current &&
      mapContainerRef.current
    ) {
      const mapRect = mapContainerRef.current.getBoundingClientRect();
      const cardRect = locationCardRef.current.getBoundingClientRect();
      const pinRef = renderedPinsRef[selectedLocation.id];
      if (pinRef?.current) {
        const pinVisible = isPinVisibleWithinMapContainer(
          pinRef.current,
          mapContainerRef.current,
        );

        if (!pinVisible) {
          locationCardRef.current.style.display = 'none';
        } else {
          const { left, top } = calculateCardPosition(
            mapRect,
            pinVisible.x,
            pinVisible.y,
            cardRect.width,
            cardRect.height,
            mapZoomLevel,
          );
          locationCardRef.current.style.display = '';
          pinRef.current.style.visibility = 'visible';
          pinRef.current.setAttribute('data-modal-open', 'true');
          locationCardRef.current.style.left = `${left}px`;
          locationCardRef.current.style.top = `${top}px`;
        }
      }
    }
  }, [
    selectedLocation,
    locationCardRef,
    mapContainerRef,
    mapZoomLevel,
    currentTranslation,
    calculateCardPosition,
    renderedPinsRef,
    isPinVisibleWithinMapContainer,
  ]);

  const renderTitle = useMemo(
    () => <Title as="h2">{commonTranslation('titles.campusMapFilters')}</Title>,
    [commonTranslation],
  );

  const renderCampusFilters = useMemo(
    () => (
      <>
        {kiosk?.model !== KioskType.ELO_TOUCH && renderTitle}
        <div className={styles.filters}>
          {kiosk?.model === KioskType.ELO_TOUCH && renderTitle}
          <div className={styles.filters__buildings}>
            <Select
              options={buildingOptions}
              onChange={(value) => handleOnChangeBuilding(value)}
              width="full"
              disabled={!buildingOptions || buildingOptions?.length === 0}
              placeholder="Building"
              selected={
                selectedBuilding
                  ? {
                      value: selectedBuilding?.id,
                      label: selectedBuilding?.name,
                    }
                  : buildingOptions?.[0]
              }
            />
          </div>
          <div className={styles.filters__floor}>
            <Select
              options={floorOptions}
              onChange={(value) => handleOnChangeFloor(value)}
              width="full"
              dropdownMobileSide="right"
              disabled={floorOptions.length === 0}
              placeholder="Floor"
              selected={
                selectedFloor
                  ? { value: selectedFloor?.id, label: selectedFloor?.name }
                  : undefined
              }
            />
          </div>
        </div>
      </>
    ),
    [
      buildingOptions,
      floorOptions,
      handleOnChangeBuilding,
      handleOnChangeFloor,
      selectedBuilding,
      selectedFloor,
      kiosk,
      renderTitle,
    ],
  );

  const renderCallToAction = useMemo(
    () => <SectionCallToAction origin="CampusDirectory" />,
    [],
  );

  const declutteredPins = useMemo(
    () => visiblePins.slice(0, MAX_POIS),
    [visiblePins],
  );

  return (
    <>
      <Modal isOpen={showDetailModal}>
        <LocationDetailsPopup
          closeButtonHandler={() => displayDetailPopupModalHandler(false)}
          location={{
            id: selectedLocation?.id ?? 0,
            name: selectedLocation?.name ?? '',
            description: selectedLocation?.description ?? '',
            image: selectedLocation?.media[0].image_url ?? '',
          }}
        />
      </Modal>
      <div
        className={`${kiosk ? styles[kiosk.model] : styles.filterMapContainer}`}
      >
        {showKeyboard && kiosk?.model !== KioskType.CTS_DIGITAL_SIGNAGE && (
          <div className={styles.mapDisabled} />
        )}
        {kiosk?.model === KioskType.ELO_TOUCH && (
          <div
            style={{
              height: 'auto',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              width: '448px',
            }}
          >
            <div>
              <Title>{selectedSite?.short_name || ''}</Title>
              {renderCampusFilters}
            </div>
            {renderCallToAction}
          </div>
        )}
        <section
          className={`${styles.sectionMain} ${
            showKeyboard && styles.clickEventsDisabled
          }`}
        >
          {kiosk?.model !== KioskType.ELO_TOUCH && renderCampusFilters}
          <div className={styles.mapContainer} ref={mapContainerRef}>
            <LocationDetailCard
              title={selectedLocation?.name ?? ''}
              image={imageResize(selectedLocation?.media[0].image_url) ?? ''}
              handleClose={() => setSelectedLocation(undefined)}
              handleShowDetails={() => displayDetailPopupModalHandler(true)}
              isOpen={!!selectedLocation}
              id={selectedLocation?.id ?? 0}
              ref={locationCardRef}
            />
            <MapView
              ref={mapViewRef}
              enableZoomControls={!!selectedFloor && !!selectedBuilding}
              calculatePinCoordinates={calculatePinCoordinates}
            >
              <img
                ref={mapImageRef}
                className={styles.map}
                src={map}
                onLoad={onMapLoad}
                data-testid="map"
                alt={
                  selectedFloor && selectedBuilding
                    ? `${selectedBuilding} ${selectedFloor} map`
                    : 'Campus Map'
                }
              />
              {declutteredPins}
              {youAreHereLocation && kioskMode && (
                <IconWithCoordinates
                  coordinates={youAreHereLocation}
                  iconType={IconType.YOU_ARE_HERE}
                />
              )}
            </MapView>
          </div>
        </section>
      </div>
    </>
  );
};

export default SectionFilterMap;
